// The map's cartographic palette, in one place.
//
// These were inlined as literals across five layer definitions, which is fine until something
// else has to agree with them — and the Route Map's legend has to agree with them exactly, or it
// documents colours the map does not draw. Values are Horizon's: the brand slate-blue ramp
// (#26404E / #365A6D / #73A2BA / #92B7C9 / #D0E0E8) for the route, the Untitled UI status ramps
// for everything that means something. The app has no dark theme, so only the light set exists.

/**
 * Trip route. Horizon primary over the light basemap; the light brand tint over satellite, where
 * a #365A6D line at 0.6 opacity disappears into dark terrain. Same line, two ends of one ramp.
 */
export const ROUTE_COLOR = { monochrome: '#365A6D', satellite: '#92B7C9' };

/**
 * The travelled portion up to the tape playhead. Theme-paired with ROUTE_COLOR, and literally its
 * inverse: the two swap ends of the Horizon ramp when the basemap does.
 *
 * Progress is drawn *over* the route at the same width, so it occludes it — but both sit on the
 * selected trip's white casing (sel-casing, width 9, beneath a width-5 line), so what each has to
 * carry is contrast against white plus enough distance from the other to read as travelled versus
 * remaining. That makes the dark step the legible one and the light step the quiet one, and which
 * of the two the route wants flips with the theme. Progress takes whichever the route did not.
 */
export const PROGRESS_COLOR = { monochrome: '#73A2BA', satellite: '#365A6D' };

/**
 * Untitled UI's status ramps, which is where the rest of the app's semantic colours come from.
 *
 * Warning 400 rather than 500 for a harsh acceleration: Warning 500 is exactly the tape's Idle
 * band, and an accel tick sitting on an idle band would be the same amber twice.
 *
 * Harsh cornering keeps its Radix purple. Untitled UI's Purple 500 is #7A5AF8, a shade off the
 * #7839EE that CHARGE_COLOR already owns, and a cornering dot that reads as a recharge on a mixed
 * fleet's map is a worse outcome than one hex outside the ramp.
 */
export const EVENT_COLOR = {
    HARSH_ACCEL: '#FDB022',
    HARSH_BRAKE: '#F04438',
    HARSH_CORNER: '#8E4EC6',
    OVERSPEED: '#175CD3',
};

export const EVENT_LABEL = {
    HARSH_ACCEL: 'Harsh acceleration',
    HARSH_BRAKE: 'Harsh braking',
    HARSH_CORNER: 'Harsh cornering',
    OVERSPEED: 'Overspeeding',
};

/**
 * Fuel ledger events. Untitled UI Success 600 / Error 600 — the gain-and-loss pairing the rest of
 * the app already reads as up and down, at the darker step so both survive a white ring.
 */
export const FUEL_COLOR = { REFILL: '#039855', DRAIN: '#D92D20' };

/** "Refuel", not "Refill": the ledger's enum is REFILL, but a fleet manager says refuel. */
export const FUEL_LABEL = { REFILL: 'Refuel', DRAIN: 'Fuel drain' };

/**
 * EV charging sessions. Violet, so it reads as neither a fill nor a drain — a recharge is the EV
 * analogue of a refuel but it is a different signal off a different sensor, and a reader scanning
 * a mixed fleet's map should not have to check the legend to tell which one a dot is.
 */
export const CHARGE_COLOR = '#7839EE';

export const CHARGE_LABEL = 'Recharge';

/**
 * Idle classes worth a dot on the map: 450 excessive and 460 excusable.
 *
 * Plain idle (400) is deliberately absent. It is the most common episode by far and drawing it
 * turns every depot into a solid mass of dots, which is what the prototype avoids by filtering it
 * out too. The Idle deck panel still lists it.
 */
export const IDLE_COLOR = { 450: '#DC6803', 460: '#FEC84B' };

export const IDLE_LABEL = { 450: 'Excessive idle', 460: 'Excusable idle' };

/** Unknown enum value — never silently reuse another category's colour. */
export const UNKNOWN_COLOR = '#667085';
